import { usePlan } from "@/entities/Plan";
import { PlanTypeInterface } from "@/entities/Plan/model/plan.types";
import { useAuth } from "@/features/Auth/lib/useAuth";
import { useNotificationActions } from "@/features/Notification";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { filterPlanTypeByUser, parseUsersInDepartment } from "@/shared/lib/users";
import { UserInterface } from "@/shared/model/api.types";
import { useEffect, useMemo, useState } from "react";
import { useHeaderDashboard } from "../../../lib/useHeaderDashboard";
import { EditPlanFormInterface } from "./EditPlanPopup.model";
import { usePlanActions } from "@/entities/Plan/lib/usePlanActions";
import { isSalesSystemAccount, salesAdministrationDepartments } from '@/features/Auth/lib/salesAccess';

const useEditPlanPopup = (id: number | null, onClose: () => void) => {
  const { myPlans, planTypes, loading: planTypesLoading, error: planTypesError } = usePlan();
  const { users, departments, subGenDepartments, loading: workspaceLoading, error: workspaceError } = useWorkspace();
  const { user, userRole } = useAuth();
  const availableDepartments = useMemo(
    () => isSalesSystemAccount(user) ? salesAdministrationDepartments(departments) : subGenDepartments,
    [departments, subGenDepartments, user]
  );
  const { topAlert, warningAlertWithConfirm } = useNotificationActions();
  const { setSelectedPlan } = useHeaderDashboard();
  const { deletePlan, update } = usePlanActions()

  const plan = id ? myPlans?.find(plan => plan.id === id) : null;

  const initForm = useMemo((): EditPlanFormInterface => {
    if (plan) {
      const { id, plan_type_id, year, month, target_value, user_id, department_id } = plan;
      return { id, plan_type_id, year, month, target_value, user_id, department_id };
    }
    return {
      id: 0,
      plan_type_id: 0,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      target_value: 0,
      user_id: 0,
      department_id: 0,
    };
  }, [plan]);

  const [form, setForm] = useState<EditPlanFormInterface>(initForm);
  const [visibleMonth, setVisibleMonth] = useState(true);
  const [filteredPlanTypes, setFilteredPlanTypes] = useState<PlanTypeInterface[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserInterface[]>([]);
  const [description, setDescription] = useState('Выберите тип плана');

  useEffect(() => {
    setForm(initForm);
  }, [initForm]);

  useEffect(() => {
    const planTypesResult = (filterPlanTypeByUser(
      planTypes,
      form && "user_id" in form ? form.user_id ?? null : null,
      availableDepartments,
      userRole,
      users
    ) ?? []).filter(pt => pt.code !== "MULTY_COMPANY_EMPLOYEE_MONTH");
    setFilteredPlanTypes(prev => {
      const isSame = Array.isArray(planTypesResult) && Array.isArray(prev) && planTypesResult.length === prev.length && planTypesResult.every((v, i) => v.id === prev[i].id);
      return isSame ? prev : (planTypesResult ?? []);
    });
    setDescription(planTypesResult.find(pt => pt.id === form.plan_type_id)?.description ?? 'Выберите тип плана')
  }, [planTypes, form.user_id, availableDepartments, userRole, users, form]);

  useEffect(() => {
    if (filteredPlanTypes && filteredPlanTypes.length > 0) {
      const hasMonthType = planTypes.find(pt => pt.id === form.plan_type_id)?.code.includes("MONTH") ?? false;
      setVisibleMonth(prev => prev === hasMonthType ? prev : hasMonthType);
    } else {
      setVisibleMonth(prev => prev === false ? prev : false);
    }
  }, [filteredPlanTypes, form.plan_type_id, planTypes]);

  useEffect(() => {
    const usersResult = parseUsersInDepartment(
      users,
      form && "department_id" in form ? (form.department_id as number) : null
    );
    setFilteredUsers(prev => {
      const isSame = Array.isArray(usersResult) && Array.isArray(prev) && usersResult.length === prev.length && usersResult.every((v, i) => v.id === prev[i].id);
      return isSame ? prev : (usersResult ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, form.department_id, form]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (!prev) return prev;
      const newForm = {
        ...prev,
        [name]:
          [
            "target_value",
            "year",
            "month",
            "plan_type_id",
            "user_id",
            "department_id",
          ].includes(name)
            ? Number(value)
            : value,
      };
      if (name === "user_id") {
        newForm.plan_type_id = 0;
      }
      const selectedPlanType = planTypes?.find((p) => p.id === newForm.plan_type_id);
      const isMonthPlan = selectedPlanType?.code.includes("MONTH");
      if (!isMonthPlan) {
        newForm.month = null;
      }
      return newForm;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // Пример ручной валидации
    if (
      !form.target_value || form.target_value === 0 ||
      !form.year || form.year === 0 ||
      (visibleMonth && !form.month) || form.month === 0 ||
      !form.department_id || form.department_id === 0 ||
      !form.user_id || form.user_id === 0 ||
      !form.plan_type_id || form.plan_type_id === 0
    ) {
      topAlert("Пожалуйста, заполните все обязательные поля", "warning", 1000);
      return;
    }
    // Подтверждение перед сохранением
    warningAlertWithConfirm(
      "Вы уверены, что хотите сохранить изменения?",
      savePlan,
      () => {},
      "Сохранить",
      "Отменить"
    );
  };

  // Реальное сохранение плана
  const savePlan = async () => {
    const res = await update(form)
    if(res){
      onClose();
    }
  };

  const [openCreatePopup, setOpenCreatePopup] = useState(false);

  const handleDelete = () => {
    warningAlertWithConfirm(
      "Вы уверены, что хотите удалить план?",
      async () => {
        const res = await deletePlan(form.id)
        if(res){
          setSelectedPlan(null);
          onClose();
        }
      },
      () => {},
      "Удалить",
      "Отменить",
      undefined,
      "danger",
      "success"
    );
  };

  return {
    form,
    users: filteredUsers,
    departments: availableDepartments,
    planTypes: filteredPlanTypes,
    visibleMonth,
    loading: {
      users: workspaceLoading.users,
      departments: workspaceLoading.departments,
      planTypes: planTypesLoading.planTypes,
      plans: planTypesLoading.myPlans
    },
    error: {
      users: workspaceError.users,
      departments: workspaceError.departments,
      planTypes: planTypesError.planTypes,
    },
    handleChange,
    handleSave,
    openCreatePopup,
    setOpenCreatePopup,
    handleDelete,
    description
  };
};

export default useEditPlanPopup;
